/**
 * Darkens a given hexadecimal color by a specified percentage.
 *
 * @param hexColor - The base color in hexadecimal format (e.g., "#FFAA00").
 * @param percent - The percentage (0 to 100) by which to darken the color.
 * @returns A new hexadecimal color string representing the darkened color.
 */
export function getDarkenColor(hexColor: string, percent: number): string {
    //INFO: Remove the '#' character if present
    const color = hexColor.startsWith('#') ? hexColor.substring(1) : hexColor;

    let r = parseInt(color.substring(0, 2), 16);
    let g = parseInt(color.substring(2, 4), 16);
    let b = parseInt(color.substring(4, 6), 16);

    //INFO: Clamp percent between 0 and 100 and convert to decimal
    const p = Math.min(100, Math.max(0, percent)) / 100;

    //INFO: Reduce each RGB component by the given percentage
    r = Math.floor(r * (1 - p));
    g = Math.floor(g * (1 - p));
    b = Math.floor(b * (1 - p));

    //INFO: Ensure RGB values stay within valid bounds
    r = Math.max(0, r);
    g = Math.max(0, g);
    b = Math.max(0, b);

    //INFO: Convert RGB values back to hexadecimal and return the result
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Lightens a hex color.
 * @param hexColor The color in hex format (e.g., "#RRGGBB").
 * @param percent The percentage to lighten (0-100). For example, 20 for 20% lighter.
 * @returns The new lightened hex color.
 *
 **/

export function getLightenColor(hexColor: string, percent: number): string {
    const color = hexColor.startsWith('#') ? hexColor.substring(1) : hexColor;

    let r = parseInt(color.substring(0, 2), 16);
    let g = parseInt(color.substring(2, 4), 16);
    let b = parseInt(color.substring(4, 6), 16);

    const p = Math.min(100, Math.max(0, percent)) / 100;

    r = Math.floor(r + (255 - r) * p);
    g = Math.floor(g + (255 - g) * p);
    b = Math.floor(b + (255 - b) * p);

    r = Math.min(255, r);
    g = Math.min(255, g);
    b = Math.min(255, b);

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
